/* eslint-disable no-mixed-operators */
/* eslint-disable no-console */

import React from 'react';
import BatteryAlert from '@mui/icons-material/BatteryAlert';
import BatteryLevel20 from '@mui/icons-material/Battery20';
import BatteryLevel30 from '@mui/icons-material/Battery30';
import BatteryLevel50 from '@mui/icons-material/Battery50';
import BatteryLevel60 from '@mui/icons-material/Battery60';
import BatteryLevel80 from '@mui/icons-material/Battery80';
import BatteryLevel90 from '@mui/icons-material/Battery90';
import BatteryLevelFull from '@mui/icons-material/BatteryFull';
import BatteryLevelUnknown from '@mui/icons-material/BatteryUnknown';
import { Typography } from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import qs from 'qs';
import moment from 'moment-timezone';
import {
  isArray, isObject, isString, keys, find, get,
  includes, isUndefined, values, sortBy, isNaN,
  isFinite, first, set, floor, isEmpty, isNil,
} from 'lodash';

import BikeSvgIcon from '../components/common/icons/bike';
import ScooterSvgIcon from '../components/common/icons/scooter';
import EBikeSvgIcon from '../components/common/icons/ebike';

export function objectToQueryString(object) {
  const lowerCaseQuery = {};
  if (object) {
    Object.keys(object).forEach((key) => {
      const lowerKey = key.toLowerCase();
      if (!object[key]) {
        return;
      }
      /* eslint-disable-next-line */
      return (lowerCaseQuery[lowerKey] = object[key]);
    });
    return qs.stringify(lowerCaseQuery);
  }
}

// from https://stackoverflow.com/questions/6048975/google-maps-v3-how-to-calculate-the-zoom-level-for-a-given-bounds
export function getBoundsZoomLevel(bounds, mapDim) {
  const WORLD_DIM = { height: 256, width: 256 };
  const ZOOM_MAX = 21;

  function latRad(lat) {
    const sin = Math.sin(lat * Math.PI / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  function zoom(mapPx, worldPx, fraction) {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  }

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

  const lngDiff = ne.lng() - sw.lng();
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

  const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

  return Math.min(latZoom, lngZoom, ZOOM_MAX);
}

export function isZagster(position) {
  if (position === 'zagster') {
    return true;
  }
  return false;
}

export function isOnMobile() {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export function getSorting(order, orderBy) {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

export function getBatteryIcon(batteryLevel) {
  if (batteryLevel === undefined || batteryLevel === '' || batteryLevel === null) {
    // test above is trying to work around values converting to 0
    return <BatteryLevelUnknown />;
  } else if (batteryLevel === 0) {
    return <BatteryAlert style={{ color: 'red' }} />;
  } else if (batteryLevel < 20) {
    return <BatteryLevel20 style={{ color: 'red' }} />;
  } else if (batteryLevel < 39) {
    return <BatteryLevel30 style={{ color: 'red' }} />;
  } else if (batteryLevel < 59) {
    return <BatteryLevel50 style={{ color: 'orange' }} />;
  } else if (batteryLevel < 69) {
    return <BatteryLevel60 style={{ color: 'orange' }} />;
  } else if (batteryLevel < 89) {
    return <BatteryLevel80 style={{ color: 'orange' }} />;
  } else if (batteryLevel < 99) {
    return <BatteryLevel90 style={{ color: 'green' }} />;
  } else {
    return <BatteryLevelFull style={{ color: 'green' }} />;
  }
}

export const FeatureFlagTypes = {
  DASHBOARD: 'Dashboard',
  ROUTE_BUILDER: 'RouteBuilder',
  VEHICLE_OUT_OF_OZ: 'VehicleOutOfOperationalZone',
  OOOZ_NOTIFICATION: 'OutOfOperationalZoneNotification',
  OOOZ_TASK: 'OutOfOperationalZoneTask',
  OOOZ_CASE: 'OutOfOperationalZoneCase',
  BEACON: 'Beacon',
  BEACON_NOTIFICATION: 'BeaconNotification',
  BEACON_TASK: 'BeaconTask',
  BEACON_CASE: 'BeaconCase',
  IDLE_VEHICLE_TASK: 'IdleVehicleTask',
  IDLE_VEHICLE_CASE: 'IdleVehicleCase',
  IDLE_VEHICLE_NOTIFICATION: 'IdleVehicleNotification',
  LOW_BATTERY_THRESHOLD: 'LowBatteryThreshold',
  LOW_BATTERY_TASK: 'LowBatteryTask',
  DATA_DRIVE: 'DataDrive',
};

const stateObject = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District Of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

export function getMatchingMarket(markets, data) {
  const matchingMarket = find(markets, (market) => market._id === data);
  return matchingMarket;
}

export function getMarketName(market = {}) {
  return market.extId ? market.extName || market.name : market.name;
}

export function stateAbbreviations() {
  return keys(stateObject);
}

export function getUserName(userObj) {
  if (!userObj._id) {
    return '';
  }
  const firstName = get(userObj, 'firstName', '');
  const lastName = get(userObj, 'lastName', '');

  return `${firstName} ${lastName}`;
}

export function getMultipleUsersNames(allUsers, userIds) {
  if (userIds && allUsers) {
    const allUserIds = allUsers.map((user) => user._id);
    const activeUserIds = userIds.filter((user) => includes(allUserIds, user));
    const userNames = activeUserIds.map((ownerId) => {
      const matchingUser = allUsers.find((user) => user._id === ownerId);
      if (matchingUser) {
        const { firstName, lastName } = matchingUser;
        return `${firstName} ${lastName}`;
      } else {
        return undefined;
      }
    });
    return userNames.join(', ');
  }
}

export function findInArray(array, itemId) {
  if (itemId) {
    const matchingItem = find(array, (item) => item._id === itemId);
    return matchingItem || '';
  } else {
    return {};
  }
}

export function sortByIntegerString(array) {
  const sorted = array.sort((a, b) => parseInt(a.humanId, 10) - parseInt(b.humanId, 10));
  return sorted || [];
}

export const timeZones = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Rome',
];

export const timeZoneHint = (timezone) => {
  if (timezone) return `${timezone} - ${moment().tz(timezone).format('z')}`;
};

export const longDateTimeFormat = 'M/D/YYYY h:mm a';
export const shortDateTimeFormat = 'M/D/YY';
export const shortDateTimeFormat2020 = 'M/D/YYYY';
export const localeLongDateTimeFormat = "MMM DD[, ']YY h:mm a";
export const localeLongDateFormat = "MMM DD[, ']YY";
export const localeTimeFormat = 'h:mm a';
export const localeFullDateFormat = 'dddd, MMM DD, YYYY';
export const localeFullYearDateFormat = 'MMM DD, YYYY';
export const localeFullDateTimeFormat = 'dddd, MMM DD YYYY h:mm a';
export const localeDateTimeFormat = 'MMM DD YYYY hh:mm a - z';
export const localeDateFormat = 'MMM. DD YYYY';
export const toLocaleStringDateFormat = 'M/D/YYYY, h:mm:ss a - z';

export function convertPolyToCoords(polygon) {
  const newCoords = [];

  polygon.forEach((point) => {
    newCoords.push([point.lng(), point.lat()]);
  });
  return newCoords;
}

export const SHOW_UNASSIGNED = 'showUnassigned';
export const UNASSIGNED = 'Not Assigned to Task';
export const ASSIGNED = 'Assigned to Task';

export const VEHICLE_EVENT_PICK_UP = 'Pick up';
export const VEHICLE_EVENT_DROP_OFF = 'Drop off';
export const VEHICLE_EVENT_VEHICLE_MISSING = 'Vehicle reported missing';
export const VEHICLE_EVENT_VEHICLE_INACCESSIBLE = 'Vehicle reported inaccessible';

// wrangler statuses
export const WRANGLER_ACTIVE = 'Active';
export const WRANGLER_DELETED = 'Deleted';
// location statuses
export const LOC_OPEN = 'Open';
export const LOC_INACTIVE = 'Inactive';
export const LOC_DELETED = 'Deleted';
// task no-recurrence
export const RECURRENCE_NONE = 'None';
// task priorities
export const HIGH_PRIORITY = 'Urgent';
export const REGULAR_PRIORITY = 'Regular';

export const TaskPriorityNames = {
  REGULAR: 'Regular',
  URGENT: 'Urgent',
};
export const taskPriorityNameValues = values(TaskPriorityNames);

export const TaskStatuses = {
  ACTIVE: 'Active',
  DONE: 'Done',
  DELETED: 'Deleted',
};
export const taskStatusValues = values(TaskStatuses);

export const TaskTypes = {
  MAINTAIN: 'Maintain',
  INSPECT: 'Inspect',
  FIND: 'Find',
  SWAP: 'Swap',
  REBALANCE: 'Rebalance',
  FIX: 'Fix',
  PICK_UP: 'Pick up',
  DROP_OFF: 'Drop off',
  STAGE: 'Stage',
};
export const taskTypeValues = values(TaskTypes);

// task asset types
export const VEHICLE = 'Vehicle';
export const VEHICLE_ZONE = 'Vehicle Zone';
export const ZONE = 'Zone';
export const LOCATION = 'Location';

// vehicle statuses
export const VEHICLE_ACTIVE = 'Active';
export const VEHICLE_CHECKED_OUT = 'Checked Out';
export const VEHICLE_MAINTENANCE = 'Maintenance';
export const VEHICLE_INACCESSIBLE = 'Inaccessible';
export const VEHICLE_MISSING = 'Missing';
export const VEHICLE_WAREHOUSE = 'Warehouse';
export const VEHICLE_LOST = 'Lost';
export const VEHICLE_DECOMMISSIONED = 'Decommissioned';

export const ALL_VEHICLE_STATUS = [
  VEHICLE_ACTIVE,
  VEHICLE_WAREHOUSE,
  VEHICLE_CHECKED_OUT,
  VEHICLE_MAINTENANCE,
  VEHICLE_INACCESSIBLE,
  VEHICLE_MISSING,
  VEHICLE_LOST,
  VEHICLE_DECOMMISSIONED,
];

// scooter types
export const ScooterTypeNames = {
  MAYFLOWER: 'MAYFLOWER',
  GALAXY: 'GALAXY',
  MIXED_TYPE: 'MIXED',
  UNKNOWN_TYPE: 'UNKNOWN',
};
export const scooterTypeValues = values(ScooterTypeNames);

// db fields
export const IN_FIELD = 'in_field';
export const WAREHOUSE = 'warehouse';
const DECOMMISSIONED = 'decommissioned';
export const IN_POSSESSION = 'in_possession';
export const LOST = 'lost';
const MISSING = 'missing';
const INACCESSIBLE = 'inaccessible';
const IN_TRANSIT = 'in_transit';
const GOVT_PARTNERSHIPS = 'govt_partnerships';
const ENGINEERING = 'engineering';
export const AVAILABLE = 'available';
export const OPERATOR = 'operator';
export const RESERVED = 'reserved';
const OUT_OF_SERVICE = 'out_of_service';
const UNAVAILABLE = 'unavailable';
const OFFLINE = 'offline';
const SELF_CHECK = 'self_check';
export const CHARGING = 'charging';
const FIRMWARE_UPDATE = 'firmware_update';
export const MISMATCH = 'mismatch';

// human readables
const InField = 'In Field';
const Warehouse = 'Warehouse';
const Decommissioned = 'Decommissioned';
const InPossession = 'In Possession';
const InTransit = 'In Transit';
const GovtPartnerships = 'Government Partnership';
const Engineering = 'Engineering';
const Lost = 'Lost';
const Missing = 'Missing';
const Inaccessible = 'Inaccessible';
const Available = 'Available';
const Operator = 'Operator';
const Reserved = 'Reserved';
const OutOfService = 'Out of Service';
const Unavailable = 'Unavailable';
const Offline = 'Offline';
const SelfCheck = 'Self Check';
const Charging = 'Charging';
const FirmwareUpdate = 'Firmware Update';
const Mismatch = 'Mismatch';

export const DEPLOYMENT_STATUS_TO_READABLE = {
  [IN_FIELD]: InField,
  [WAREHOUSE]: Warehouse,
  [DECOMMISSIONED]: Decommissioned,
};
export const POSSESSION_STATUS_TO_READABLE = {
  [IN_POSSESSION]: InPossession,
  [LOST]: Lost,
  [MISSING]: Missing,
  [INACCESSIBLE]: Inaccessible,
  [IN_TRANSIT]: InTransit,
  [GOVT_PARTNERSHIPS]: GovtPartnerships,
  [ENGINEERING]: Engineering,
};
export const EXT_STATUSES_TO_READABLE = {
  [AVAILABLE]: Available,
  [OPERATOR]: Operator,
  [RESERVED]: Reserved,
  [OUT_OF_SERVICE]: OutOfService,
  [UNAVAILABLE]: Unavailable,
  [OFFLINE]: Offline,
  [SELF_CHECK]: SelfCheck,
  [CHARGING]: Charging,
  [FIRMWARE_UPDATE]: FirmwareUpdate,
  [MISMATCH]: Mismatch,
};

export const DEPLOYMENT_STATUSES = keys(DEPLOYMENT_STATUS_TO_READABLE);
export const getDeploymentStatusReadable = (deploymentStatus) => get(DEPLOYMENT_STATUS_TO_READABLE, deploymentStatus, '');
export const POSSESSION_STATUSES = keys(POSSESSION_STATUS_TO_READABLE);
export const getPossessionStatusReadable = (possessionStatus) => get(POSSESSION_STATUS_TO_READABLE, possessionStatus, '');

export const EXT_STATUSES = keys(EXT_STATUSES_TO_READABLE);
export const getExtStatusReadable = (extStatus) => get(EXT_STATUSES_TO_READABLE, extStatus, '');

// vehicle types
export const BIKE = 'Bike';
export const SCOOTER = 'Scooter';
export const EBIKE = 'eBike';
export const VEHICLE_TYPE_VALUES = [BIKE, SCOOTER, EBIKE];
export const VEHICLE_BATTERY_TYPE_VALUES = [SCOOTER, EBIKE];

export const OpsBrandNames = {
  ZAGSTER: 'Zagster',
  SPIN: 'Spin',
  LINK: 'Link',
//  BERYL: 'Beryl',
};
export const opsBrandNameValues = values(OpsBrandNames);

export const OpsZoneStatuses = {
  ACTIVE: 'Active',
  DELETED: 'Deleted',
};
export const opsZoneStatusValues = values(OpsZoneStatuses);

export const ZoneHexColors = {
  LIGHT_NAVY: '#5A7981',
  GREEN: '#49801D',
  OLIVE: '#7D791C',
  ORANGE: '#C55030',
  LIGHT_BROWN: '#A16721',
  DARK_MAROON: '#54494B',
  AQUA: '#41817D',
  ROSE: '#9C0D38',
  PURPLE: '#704F7D',
  SIENA: '#9C3F26',
  DARK_FUCHSIA: '#A43574',
  LIGHT_RED: '#CC334F',
  BURGUNDY: '#6D0927',
  DARK_GREEN: '#4B5449',
  LIGHT_PURPLE: '#886AA0',
  VIOLET: '#6655A4',
  BLUE: '#353CA4',
  DARK_BROWN: '#6E3C00',
};

export const zobaHexColor = '#000000';

export const VehicleZonesStatusNames = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  DELETED: 'Deleted',
};
export const vehicleZonesStatusNamesValues = values(VehicleZonesStatusNames);

export const VehicleZoneCategories = {
  CITY_BOUNDARY: 'City Boundary', // deprecated in favor of Operational
  NO_RIDE: 'No Ride', // deprecated in favor of No Ride
  SLOW_RIDE: 'Slow Ride', // deprecated in favor of Slow Riding
  PICKUP: 'Pick Up',
  OPERATIONAL_AREA: 'Operational Area',
  NO_SCOOTER: 'No Scooter',
  NO_RIDING: 'No Riding',
  NO_PARKING: 'No Parking',
  NO_PARKING_SLOW_RIDING: 'No Parking, Slow Riding',
  SLOW_RIDING: 'Slow Riding',
  LIMITED_PARKING: 'Limited Parking',
  PARKING_AREA: 'Parking Area',
  WAREHOUSE: 'Warehouse',
  PARKING_INCENTIVE_ZONE: 'Parking Incentive',
};

export const VehicleZoneTypes = {
  PICKUP: 'Pickup',
  EQUITY: 'Equity',
  HIGH_RISK: 'High Risk',
};

export const LinkAdminVehicleZoneCategories = {
  OPERATIONAL_AREA: 'Operational zone',
  WAREHOUSE: 'Warehouse',
  NO_PARKING: 'No parking zone',
  NO_SCOOTER: 'No scooter zone',
  NO_RIDING: 'No riding zone',
  SLOW_RIDING: 'Slow riding zone',
  NO_PARKING_SLOW_RIDING: 'No parking slow zone',
  PARKING_AREA: 'Parking allowed zone',
  LIMITED_PARKING: 'Parking limited zone',
  PARKING_INCENTIVE_ZONE: 'Parking incentivized zone',
  PICKUP: 'Pickup zone',
};

export const vehicleZoneCategories = values(VehicleZoneCategories);
export const linkAdminVehicleZoneCategories = values(LinkAdminVehicleZoneCategories);
export const editableVehicleZoneCategories = [VehicleZoneCategories.PICKUP];

export const OpsBrandNamesLocationTargets = {
  ZAGSTER: OpsBrandNames.ZAGSTER.toLowerCase(),
  SPIN: OpsBrandNames.SPIN.toLowerCase(),
  LINK: OpsBrandNames.LINK.toLowerCase(),
};

export const VEHICLE_TYPES = {
  SCOOTER: 'Scooter',
  BIKE: 'Bike',
  EBIKE: 'eBike',
};
export const vehicleTypeNameValues = values(VEHICLE_TYPES);

export const RepairTicketStatuses = {
  OPEN: 'Open',
  CLOSED: 'Closed',
};
export const CategoryOrErrorCode = {
  CATEGORY: 'Category',
  ERROR_CODE: 'Error Code',
};

export const repairTicketStatusValues = values(RepairTicketStatuses);

export const LOCKED = 'Locked';
export const UNLOCKED = 'Unlocked';
export const UNKNOWN = 'Unknown';

export function mapLockStringToBoolean(lockStatus) {
  if (lockStatus === LOCKED) {
    return true;
  } else if (lockStatus === UNKNOWN) {
    return undefined;
  } else if (lockStatus === UNLOCKED) {
    return false;
  }
}

export function mapLockStateToString(lockStatus) {
  if (lockStatus) {
    return LOCKED;
  } else if (isUndefined(lockStatus)) {
    return UNKNOWN;
  } else if (lockStatus === false) {
    return UNLOCKED;
  }
}

export function getMomentMillis(date) {
  return moment(date).format('x');
}

export function formatUtcDate(date) {
  return moment.utc(date).format(longDateTimeFormat);
}

export const RunTaskTypes = {
  PICKUP: 'Pick up',
  DEPLOY: 'Deploy',
  MISSING: 'Missing',
  INACCESSIBLE: 'Inaccessible',
  DAMAGED: 'Damaged',
};

export const RunTaskStatuses = {
  OPEN: 'Open',
  COMPLETED: 'Completed',
  ATTEMPTED: 'Attempted',
  FAILED: 'Failed',
};

export const VehicleEventTypes = {
  VEHICLE_LOST: 'Vehicle reported lost',
  VEHICLE_MISSING: 'Vehicle reported missing',
  VEHICLE_IN_TRANSIT: 'Vehicle reported in transit',
  VEHICLE_GOVT_PARTNERSHIPS: 'Vehicle reported as unavailable by city request',
  VEHICLE_ENGINEERING: 'Vehicle reported in engineering',
  PICK_UP: 'Pick up',
  DROP_OFF: 'Drop off',
  REBALANCE_PICK_UP: 'Rebalance pick up',
  REBALANCE_DROP_OFF: 'Rebalance drop off',
  SAFETY_INSPECTION: 'Safety inspection',
  INVENTORY_SCAN: 'Inventory Scan',
  ADMIN_VEHICLE_UPDATE: 'Admin Vehicle Update',
  ADMIN_BULK_VEHICLE_UPDATE: 'Admin Bulk Vehicle Update',
  START_RIDE: 'Start ride',
  END_RIDE: 'End ride',
  DECOMMISSIONED: 'Decommissioned',
  PARTNER_SYNC: 'Partner sync',
  PARTNER_STATUS_CHANGE: 'Partner Status Change',
};
export const vehicleEventTypes = values(VehicleEventTypes);
export const WranglerRoleNames = {
  ADMIN: 'Admin',
  CORPORATE_OM: 'Corporate OM',
  MARKET_OM: 'Market OM',
  BRAND_PARTNER: 'Brand Partner',
  OPERATIONS_ASSOCIATE: 'Operations Associate',
  PROPERTY_MANAGER: 'Property Manager',
  MECHANIC: 'Mechanic',
  TPL_MANAGER: '3PL Manager',
};
export const wranglerRoleNames = values(WranglerRoleNames);
export const WranglerLoginStatuses = {
  LOGGED_IN: 'Logged In',
  LOGGED_OUT: 'Logged Out',
};
export const wranglerLoginStatuses = values(WranglerLoginStatuses);

export const WranglerStatuses = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};
export const wranglerStatuses = values(WranglerStatuses);

export const FieldReportStatuses = {
  NEW: 'New',
  OPEN: 'Open',
  CLOSED: 'Closed',
};
export const fieldReportStatusValues = values(FieldReportStatuses);
export const fieldReportStatusesForSelection = fieldReportStatusValues.filter(
  (status) => status !== FieldReportStatuses.NEW,
);

// resolution codes are ways to tell LA311 that issue is resolved
export const FieldReportResolutionCodes = {
  VPC: 'Vehicle Parked Correctly',
  NOV: 'Not Operator Vehicle',
  VNL: 'Vehicle Not at Location',
  VM: 'Vehicle moved to appropriate parking location',
  OPUVM: 'Operator Picked up vehicle for maintenance',
  VMRB: 'Vehicle moved for rebalancing',
};
export const fieldReportResolutionCodeValues = keys(FieldReportResolutionCodes);

export const NEW_FIELD_REPORT_COLOR = '#e5e5e5';
export const NEW_FIELD_REPORT_STATUS_COLOR = '#f44336';
export const CLOSED_FIELD_REPORT_STATUS_COLOR = '#ffa726';
export const CLOSED_STATUS_COLOR = '#66bb6a';
export const CLOSED_REPAIR_TICKET_STATUS_COLOR = '#FFF';

export function getStatusStringColor(status, repair = false) {
  switch (status) {
    case FieldReportStatuses.CLOSED:
      return CLOSED_STATUS_COLOR;
    case FieldReportStatuses.NEW:
      return NEW_FIELD_REPORT_STATUS_COLOR;
    case FieldReportStatuses.OPEN:
      return repair ? CLOSED_REPAIR_TICKET_STATUS_COLOR : CLOSED_FIELD_REPORT_STATUS_COLOR;
    default:
      return '#90caf9';
  }
}

export const MarketStatuses = {
  ACTIVE: 'Active',
  CLOSED: 'Closed',
};
export const marketStatusValues = values(MarketStatuses);

export const TaskSwapTypes = {
  BATTERY: 'Battery',
  VEHICLE: 'Vehicle',
};
export const taskSwapTypeNames = values(TaskSwapTypes);

export function doesTaskHaveVehicleWithMatchingType(task, vehicleType) {
  if (!task.assignedVehicles) return false;

  const match = task.assignedVehicles.find((v) => v.type === vehicleType);
  return !!match;
}

export function doesTaskHaveVehicleWithMatchingBrand(task, vehicleBrand) {
  if (!task.assignedVehicles) return false;

  const match = task.assignedVehicles.find((v) => v.brand === vehicleBrand);
  return !!match;
}

export function doesTaskHaveVehicleWithMatchingStatus(task, status) {
  if (!task.assignedVehicles) return false;

  const match = task.assignedVehicles.find((v) => v.status === status);
  return !!match;
}

export function renderNumberAsString(number, decimals = 1) {
  if (isNaN(number) || !isFinite(number)) return '0';
  if (number) {
    const formattedData = (number).toFixed(decimals).replace(/\.0/, '');
    return `${formattedData}`;
  }
  return '0';
}

export function renderNumberAsPercentString(number, decimals = 1) {
  if (isNaN(number)) return '0%';
  else if (!Number.isFinite(number)) return '0%';
  else if (number) {
    const formattedData = (number * 100).toFixed(decimals).replace(/\.0/, '');
    return `${formattedData}%`;
  }
  return '0%';
}

export const DEFAULT_TARGET_DEPLOYMENT = 100;
export function findTargetDeploymentForDate(tz, targetDeployments, targetDate) {
  const requestedDate = moment.tz(targetDate, tz).startOf('day');

  /*
    1. Sort by (0 - epoch) so later times come up first
    2. Iterate starting with most recent to find the first time that is on or before the target date
    3. return that targetDeployment, or DEFAULT_TARGET_DEPLOYMENT if not found
   */
  const sortedTargets = sortBy(
    targetDeployments,
    (target) => 0 - moment(target.effectiveOn).valueOf(),
  ); // uses epoch millis, no tz needed
  const targetDeployment = find(sortedTargets, (target) => {
    const currentTargetTime = moment.tz(target.effectiveOn, tz).startOf('day');
    return currentTargetTime.isSameOrBefore(requestedDate);
  });

  return isUndefined(targetDeployment)
    ? { target: DEFAULT_TARGET_DEPLOYMENT, effectiveOn: requestedDate }
    : targetDeployment;
}
export function getTargetDeployment(market, targetDate) {
  const tz = get(market, 'tz', timeZones[0]);
  const targets = get(market, 'targetDeployments', []);
  return findTargetDeploymentForDate(tz, targets, targetDate);
}
export const DEFAULT_ZAGSTER_CONTRACTED_VEHICLES = 9999;
export function getZagsterContractedVehicles(market, report) {
  let targetQuantity = get(report, 'targetQuantity');
  if (isUndefined(targetQuantity)) targetQuantity = get(market, 'zagsterContractedVehicles', DEFAULT_ZAGSTER_CONTRACTED_VEHICLES);
  return targetQuantity;
}

export function shouldFetchList(marketId, dataList, checkFirstForMarketFn) {
  // does the list exist? does the data we have match the market we're in?
  if (isUndefined(dataList)) {
    return true;
  } else if (checkFirstForMarketFn) {
    checkFirstForMarketFn(marketId, first(dataList));
  } else {
    const dataMarketId = get(first(dataList), 'marketId');
    if (!isUndefined(dataMarketId) && dataMarketId !== marketId) return true;
  }
  return false;
}

export const LAST_SERVICE_FIELD = 'lastServiceDate';
export const LAST_MOVE_FIELD = 'lastMove';
export const LAST_TRIP_FIELD = 'lastTrip';
export const LAST_ACTIVITY_FIELD = 'lastActivity';
export const LAST_DEPLOY_FIELD = 'lastDeploy';
export const LAST_PICKUP_FIELD = 'lastPickup';

export function metersToMiles(meters) {
  return meters / 1609.344; // 1609.344 meters to a mile
}

export function getUrlSearchResults() {
  return qs.parse(window.location.search, { ignoreQueryPrefix: true });
}

export function urlParamFilter(argName, urlColumnMap, parsedUrl, columnData, selectedFilters) {
  const value = get(parsedUrl, argName);
  if (value === undefined) return;

  // check if map result is a string; if it isn't, use mapResult.columnName to find the idx
  const columnMapResult = urlColumnMap[argName];
  const columnName = isString(columnMapResult) ? columnMapResult : get(columnMapResult, 'columnName');
  const idx = columnData.findIndex((column) => column.name === columnName);
  if (idx === undefined) return;

  if (isArray(value)) {
    set(selectedFilters, idx, value);
  } else {
    // if the map result is an object, get the filter's index for a custom filter; otherwise, default to 0 for a normal filter
    const filterIdx = get(columnMapResult, 'filterIdx', 0);
    set(selectedFilters, `[${idx}][${filterIdx}]`, value);
  }

  return selectedFilters;
}

export function rideMinutesForDisplay(rideSeconds) {
  if (rideSeconds === 0) {
    return 0;
  } else if (rideSeconds < 60) {
    return '< 1';
  } else if (rideSeconds > 60) {
    return floor(rideSeconds / 60);
  }
}

const datetimeFormatWithYear = 'MM/DD/YY hh:mma';
const datetimeFormatNoYear = 'MM/DD hh:mma';

export function dateFormatForRides(date) {
  const currentYear = moment()
    .year();
  const yearToCompare = moment(date)
    .year();
  if (yearToCompare === currentYear) {
    return datetimeFormatNoYear;
  } else {
    return datetimeFormatWithYear;
  }
}

export const LocationTypeNames = {
  STATION: 'Station',
  HUB: 'Hub',
};
export const locationTypeNames = values(LocationTypeNames);

export const AutomatedPickupTaskReasons = {
  CRITICAL_BATTERY: 'Critical Battery',
  FAULT: 'Fault',
  OFFLINE: 'Offline',
  RESERVATION_LIMIT: 'Reservation Limit',
  TEMPERATURE: 'Temperature',
  RIDER_SCAN: 'Rider Scan',
};
export const automatedPickupTaskReasonNames = values(AutomatedPickupTaskReasons);
export const ManualPickupTaskReasons = {
  LOW_BATTERY: 'Low Battery',
  BAD_PARKING: 'Bad Parking',
  USER_REPORT: 'User Report',
  REBALANCE: 'Rebalance',
  SEARCH: 'Search',
  RECALL: 'Recall',
  INCIDENT: 'Incident',
  OTHER: 'Other',
  MAINTENANCE: 'Maintenance',
};
export const manualPickupTaskReasonNames = values(ManualPickupTaskReasons);
export const pickupTaskReasonNames = [
  ...manualPickupTaskReasonNames,
  ...automatedPickupTaskReasonNames,
];

export const InspectTaskReasonNames = { ROLLOVER: 'Rollover' };
export const inspectTaskReasonNames = values(InspectTaskReasonNames);

export const FilterInspectTaskReasonNames = {
  NONE: 'None',
  ...InspectTaskReasonNames,
};
export const filterInspectTaskReasonNames = values(FilterInspectTaskReasonNames);

export const PICKUP_REASON_DEFAULT = ManualPickupTaskReasons.LOW_BATTERY;

export const RepairSeverityTypes = {
  YELLOW: 'Yellow',
  ORANGE: 'Orange',
  RED: 'Red',
};
export const repairSeverityTypes = values(RepairSeverityTypes);

const YELLOW_SEVERITY_COLOR = '#FBEC5D';
const ORANGE_SEVERITY_COLOR = '#ED9121';
const RED_SEVERITY_COLOR = '#FF6666';
const SEVERITY_COLOR_DEFAULT = 'A9A9A9';

export function getSeverityStringColor(severity) {
  switch (severity) {
    case RepairSeverityTypes.YELLOW:
      return YELLOW_SEVERITY_COLOR;
    case RepairSeverityTypes.ORANGE:
      return ORANGE_SEVERITY_COLOR;
    case RepairSeverityTypes.RED:
      return RED_SEVERITY_COLOR;
    default:
      return SEVERITY_COLOR_DEFAULT;
  }
}
export function convertTimeStringToReadable(str) {
  if (!str) return '';
  const [hrs, mins] = str.split(':');
  const amPm = hrs < 12 ? 'AM' : 'PM';
  let hrsCleaned = hrs <= 12 ? hrs : hrs - 12;
  if (hrs === '00') {
    hrsCleaned = 12;
  }
  return `${hrsCleaned}:${mins}${amPm}`;
}

export const iconForType = {
  [BIKE]: <BikeSvgIcon />,
  [EBIKE]: <EBikeSvgIcon />,
  [SCOOTER]: <ScooterSvgIcon />,
};

export function formatPhoneNumberForDisplay(phoneNumberString) {
  const cleaned = (phoneNumberString).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return null;
}

export function getTargetVehicleCounts(targetVehicleCounts) {
  if (!isObject(targetVehicleCounts)) return;

  const counts = Object.values(targetVehicleCounts);
  return counts.reduce((current, total) => current + total);
}

export const BOTH_APPS = 'Both';
const NONE = 'None';
export const IOS = 'ios';

export function getUsersAppCenterLists(wrangler) {
  const hasIOS = get(wrangler, 'appCenter.ios.active', false);

  if (hasIOS) {
    return IOS;
  } else {
    return NONE;
  }
}

export const SMALL_PROFILE_PHOTO_SIZE = 35;
export const REGULAR_PROFILE_PHOTO_SIZE = 50;
export const LARGE_PROFILE_PHOTO_SIZE = 200;
export const DEFAULT_PROFILE_PHOTO_SIZE = REGULAR_PROFILE_PHOTO_SIZE;

export const PICKUP_FIELDS = {
  ALL_VEHICLES: 'all_vehicles',
  ALL_PICKUPS: 'all_pickups',
  URGENT_ONLY: 'urgent_only',
  NOT_FOR_PICKUP: 'not_for_pickup',
  ALL_PICKUPS_INSPECTS: 'all_pickups_inspects',
};

export function searchMultipleVehicles(vehicles, searchText) {
  const searchTextLowerCase = searchText.map((vehicle) => vehicle.toLowerCase());
  return vehicles.filter((vehicle) => {
    let vehicleList;
    searchTextLowerCase.forEach((vehicleSearchText) => {
      if (vehicle.humanId.toLowerCase() === vehicleSearchText) {
        vehicleList = true;
      }
    });
    return vehicleList;
  });
}

export const RunStatusNames = {
  TODO: 'To Do',
  EXPIRED: 'Expired',
  IN_PROGRESS: 'In Progress',
  CLOSED: 'Closed',
  DELETED: 'Deleted',
};
export const runStatusNames = values(RunStatusNames);

export const DataDriveRunTypes = {
  DEPLOY: '0',
  REBALANCE: '1',
  EQUITY_DEPLOY: '2',
  DEPLOY_AND_REBALANCE: '3',
  EQUITY_DEPLOY_AND_REBALANCE: '4',
  DEPLOYMENT_ONE_WAY: '5',
};
export const dataDriveRunTypeValues = values(DataDriveRunTypes);

export function getTitleAndDataCount(title, vehicleData) {
  const displayCount = get(vehicleData, 'length', 0); // fall back to table data value
  return (
    <Typography variant="h6">
      {title}
        &nbsp;(
      {displayCount}
        &nbsp;Results)
    </Typography>
  );
}

export const RunScheduleStatusNames = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  DELETED: 'Deleted',
};
export const runScheduleStatusNames = values(RunScheduleStatusNames);

export const runTypes = [
  { label: 'Deployment', value: DataDriveRunTypes.DEPLOY },
  { label: 'Rebalance', value: DataDriveRunTypes.REBALANCE },
  { label: 'Deployment + Rebalance', value: DataDriveRunTypes.DEPLOY_AND_REBALANCE },
  { label: 'Deployment (one way)', value: DataDriveRunTypes.DEPLOYMENT_ONE_WAY },
];
export const sortedRunTypes = sortBy(runTypes, (type) => type.label.toLowerCase());

export const daysObject = {
  0: 'Su',
  1: 'M',
  2: 'T',
  3: 'W',
  4: 'Th',
  5: 'F',
  6: 'S',
};

export function getDurationForDynamicRun(run, market) {
  const runType = get(run, 'type');
  const deploymentDuration = get(market, 'marketDeploymentDuration', 2.5);
  const rebalanceDuration = get(market, 'marketRebalanceDuration', 2.5);
  switch (runType) {
    case DataDriveRunTypes.REBALANCE:
      return rebalanceDuration;
    case DataDriveRunTypes.DEPLOY_AND_REBALANCE:
      return deploymentDuration + rebalanceDuration;
    case DataDriveRunTypes.DEPLOY:
    case DataDriveRunTypes.DEPLOYMENT_ONE_WAY:
    case DataDriveRunTypes.EQUITY_DEPLOY:
      return deploymentDuration;
    default:
      return 2.5;
  }
}

export const advanceFilterLabels = {
  maintenanceFilter: 'Maintenance Flag',
  hasDefaultGpkgFilter: 'Geofence Package',
  minBatteryFilter: 'Min Battery Level',
  maxBatteryFilter: 'Max Battery Level',
  pickupFieldsFilter: {
    all_vehicles: 'All Scooters',
    all_pickups: 'All for Pickup',
    urgent_only: 'Urgent Pickups',
    not_for_pickup: 'Not For Pickup',
    all_pickups_inspects: 'All for Pickup or Inspect',
  },
  fleetFilter: 'Fleet',
  taskAssigneesFilter: 'Task Assignee',
  pickupReasonsFilter: 'Pickup Reason',
  pickupZonesFilter: 'Zone',
  locationFilter: 'Location',
  geofenceLayersFilter: 'Geofence Layers',
  inspectReasonsFilter: 'Inspect Reason',
  hardwareFilter: 'Hardware',
  scooterTypeFilter: 'Scooter Type',
  lastPickupFilterBefore: 'Last Pickup Before',
  lastPickupFilterAfter: 'Last Pickup After',
  lastDeployFilterBefore: 'Last Deploy Before',
  lastDeployFilterAfter: 'Last Deploy After',
  lastTripFilterBefore: 'Last Trip Before',
  lastTripFilterAfter: 'Last Trip After',
  lastCommunicationFilterBefore: 'Last Communication Before',
  lastCommunicationFilterAfter: 'Last Communication After',
  lastRiderScanFilterBefore: 'Last Rider Scan Before',
  lastRiderScanFilterAfter: 'Last Rider Scan',
  minSearchAttemptsFilter: 'Min Search Attempts',
  maxSearchAttemptsFilter: 'Max Search Attempts',
  lastReportedNetworkFilter: 'Cellular Network',
};

export const LOCATION_FILTER_FIELDS = {
  IN_COMPLIANT_HUB: 'In compliant hubs',
  IN_OVER_CAPACITY_HUB: 'In overcapacity hubs',
  OUTSIDE_OF_HUBS: 'Outside of hubs',
  NOVE_OF_THE_ABOVE: 'None of the Above',
};
export const locationFilterFieldsValues = values(LOCATION_FILTER_FIELDS);

export const MAINTENACE_FILTER_FIELDS = {
  YES: 'Yes',
  NO: 'No',
  NOVE_OF_THE_ABOVE: 'None of the Above',
};
export const maintenanceFilterFieldsValues = values(MAINTENACE_FILTER_FIELDS);

export const HAS_DEFAULT_GPKG_FILTER_FIELDS = {
  SAME: 'Same as the market default',
  NOT_SAME: 'Not the same as the market default',
};

export const hasDefaultGpkgFilterFieldsValues = values(HAS_DEFAULT_GPKG_FILTER_FIELDS);

export const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
    marginRight: 2,
    cursor: 'default',
  },
}));

export function filterVehicles(vehicles, filters, vehicleActivities) {
  const {
    deploymentFilters,
    possessionFilters,
    extStatusFilters,
    maintenanceFilter,
    hasDefaultGpkgFilter,
    minBatteryFilter,
    maxBatteryFilter,
    pickupFieldsFilter,
    fleetFilter,
    taskAssigneesFilter,
    pickupReasonsFilter,
    pickupZonesFilter,
    geofenceLayersFilter,
    inspectReasonsFilter,
    hardwareFilter,
    scooterTypeFilter,
    lastPickupFilterBefore,
    lastPickupFilterAfter,
    lastDeployFilterBefore,
    lastDeployFilterAfter,
    lastTripFilterBefore,
    lastTripFilterAfter,
    lastCommunicationFilterBefore,
    lastCommunicationFilterAfter,
    lastRiderScanFilterBefore,
    lastRiderScanFilterAfter,
    minSearchAttemptsFilter,
    maxSearchAttemptsFilter,
    lastReportedNetworkFilter,
    locationFilter,
  } = filters;

  return vehicles.filter((vehicle) => {
    const DEBUG = false;
    const {
      extStatus,
      extDeploymentStatus,
      extPossessionStatus,
      batteryLevel,
      needsMaintenance,
      geoUpdatedAt,
      lastScanTime,
      lastPickupZoneIds = [],
      tasks = [],
      searchAttempts = [],
      hasStateMismatch = false,
      locationId,
      location = [],
      hasMarketDefaultGeofencePackage,
    } = vehicle;
    const matchExtStatusFilter = (hasStateMismatch && includes(extStatusFilters, MISMATCH))
      || includes(extStatusFilters, extStatus);
    const matchExtDeploymentStatusFilter = includes(deploymentFilters, extDeploymentStatus);
    const matchExtPossessionStatusFilter = includes(possessionFilters, extPossessionStatus);
    const hasBatteryLevel = batteryLevel || batteryLevel === 0;
    const isStatusMatch = matchExtStatusFilter
      && matchExtDeploymentStatusFilter
      && matchExtPossessionStatusFilter;
    const pickupTasks = tasks.filter(
      (task) => task.task === TaskTypes.PICK_UP
      && task.pickupReason,
    );
    const hasPickupTask = pickupTasks && pickupTasks.length > 0;
    const hasUrgentPickupTask = pickupTasks.find((t) => t.priority === TaskPriorityNames.URGENT);
    const hasSearchAttempts = searchAttempts.length || searchAttempts.length === 0;
    const inspectTasks = tasks.filter(
      (task) => task.task === TaskTypes.INSPECT
      && task.inspectReason,
    );
    const hasInspectTask = inspectTasks && inspectTasks.length > 0;
    const lastTrip = get(vehicleActivities, `${vehicle._id}.${LAST_TRIP_FIELD}`, ''); // lastTrip
    const lastPickup = get(vehicleActivities, `${vehicle._id}.${LAST_PICKUP_FIELD}`, ''); // lastPickup
    const lastDeploy = get(vehicleActivities, `${vehicle._id}.${LAST_DEPLOY_FIELD}`, ''); // lastDeploy
    const lastCommunication = geoUpdatedAt;
    const loc = !isEmpty(location) ? location[0] : [];
    const aggregateTarget = getTargetVehicleCounts(loc.targetVehicleCounts);
    const overCapacity = loc?.vehicleIds?.length > aggregateTarget;
    if (!isStatusMatch) {
      if (DEBUG) console.info('vehicle fails status filter', vehicle.humanId, extStatus, extDeploymentStatus, extPossessionStatus);
      return false;
    }
    if (maintenanceFilter && maintenanceFilter.length > 0) {
      const hasMaintenanceFlag = maintenanceFilter === MAINTENACE_FILTER_FIELDS.YES;
      const hasNoMaintenanceFlag = maintenanceFilter === MAINTENACE_FILTER_FIELDS.NO;
      if (hasMaintenanceFlag) {
        if (!needsMaintenance) {
          if (DEBUG) console.info('vehicle fails maintenance filter', vehicle.humanId, needsMaintenance);
          return false;
        }
      }
      if (hasNoMaintenanceFlag) {
        if (needsMaintenance) {
          if (DEBUG) console.info('vehicle fails no maintenance filter', vehicle.humanId, needsMaintenance);
          return false;
        }
      }
    }
    if (hasBatteryLevel) {
      const isLowerThanMinFilter = batteryLevel < minBatteryFilter;
      const isHigherThanMaxFilter = batteryLevel > maxBatteryFilter;
      if (isLowerThanMinFilter || isHigherThanMaxFilter) {
        if (DEBUG) console.info('vehicle fails battery filter', vehicle.humanId, vehicle.batteryLevel);
        return false;
      }
    }
    if (pickupFieldsFilter === PICKUP_FIELDS.ALL_PICKUPS) {
      if (!hasPickupTask) {
        if (DEBUG) console.info('vehicle fails all pickups filter', vehicle.humanId);
        return false;
      }
    } else if (pickupFieldsFilter === PICKUP_FIELDS.URGENT_ONLY) {
      if (!hasUrgentPickupTask) {
        if (DEBUG) console.info('vehicle fails urgent pickup task filter', vehicle.humanId);
        return false;
      }
    } else if (pickupFieldsFilter === PICKUP_FIELDS.NOT_FOR_PICKUP) {
      if (hasPickupTask) {
        if (DEBUG) console.info('vehicle fails not for pickup filter', vehicle.humanId);
        return false;
      }
    } else if (pickupFieldsFilter === PICKUP_FIELDS.ALL_PICKUPS_INSPECTS) {
      if (!hasPickupTask && !hasInspectTask) {
        if (DEBUG) console.info('vehicle fails not for pickup or inspect filter', vehicle.humanId);
        return false;
      }
    }
    if (fleetFilter && fleetFilter.length > 0) {
      const hasFleet = vehicle.childMarket && fleetFilter.includes(vehicle.childMarket._id);
      if (!hasFleet) {
        if (DEBUG) console.info('vehicle fleet reasons filter', vehicle.humanId);
        return false;
      }
    }
    if (taskAssigneesFilter && taskAssigneesFilter.length > 0) {
      const showUnassigned = taskAssigneesFilter.includes('Unassigned');
      const showAssigned = taskAssigneesFilter.includes('Anyone');
      const hasMatchingAssignee = tasks.find((t) => {
        const match = t.assignedToIds.filter((assignee) => taskAssigneesFilter.includes(assignee));
        return match.length > 0;
      });
      const assigneeCount = tasks.map((t) => t.assignedToIds.length)
        .reduce((acc, current) => acc + current, 0);
      if (!showAssigned && !showUnassigned) {
        if (!hasMatchingAssignee) {
          if (DEBUG) console.info('vehicle fails task assignees filter', vehicle.humanId);
          return false;
        }
      } else if (!showAssigned && showUnassigned) {
        if (assigneeCount > 0 && !hasMatchingAssignee) {
          if (DEBUG) console.info('vehicle fails task assignees filter', vehicle.humanId);
          return false;
        }
      } else if (showAssigned && !showUnassigned) {
        if (assigneeCount === 0) {
          if (DEBUG) console.info('vehicle fails task assignees filter', vehicle.humanId);
          return false;
        }
      }
    }
    if (pickupReasonsFilter && pickupReasonsFilter.length > 0) {
      const hasMatchingTask = pickupTasks.find((t) => pickupReasonsFilter.includes(t.pickupReason));
      if (!hasMatchingTask) {
        if (DEBUG) console.info('vehicle fails pickup reasons filter', vehicle.humanId);
        return false;
      }
    }
    if (pickupZonesFilter && pickupZonesFilter.length > 0) {
      const hasMatchingZone = lastPickupZoneIds.find((t) => pickupZonesFilter.includes(t));
      if (!hasMatchingZone) {
        if (DEBUG) console.info('vehicle fails zones filter', vehicle.humanId);
        return false;
      }
    }
    if (inspectReasonsFilter && inspectReasonsFilter.length > 0) {
      const hasMatchingTask = inspectReasonsFilter.includes('None') && !inspectTasks.length
        || inspectTasks.find((t) => inspectReasonsFilter.includes(t.inspectReason));
      if (!hasMatchingTask) {
        if (DEBUG) console.info('vehicle fails inspect reasons filter', vehicle.humanId);
        return false;
      }
    }
    if (hardwareFilter && hardwareFilter.length > 0) {
      const hasHardware = hardwareFilter.includes('Seat') && vehicle.seatInstalled;
      if (!hasHardware) {
        if (DEBUG) console.info('vehicle fails hardware filter', vehicle.humanId);
        return false;
      }
    }
    if (scooterTypeFilter && scooterTypeFilter.length > 0) {
      const hasScooterType = scooterTypeFilter.includes(vehicle.scooterType);
      if (!hasScooterType) {
        if (DEBUG) console.info('vehicle fails scooter type filter', vehicle.humanId);
        return false;
      }
    }
    if (lastPickupFilterBefore) {
      if ((!lastPickup || moment(lastPickup).isAfter(lastPickupFilterBefore))
        && (lastPickupFilterAfter || lastPickup)) {
        if (DEBUG) console.info('vehicle fails lastPickup before filter', vehicle.humanId);
        return false;
      }
    }
    if (lastPickupFilterAfter) {
      if (!lastPickup || moment(lastPickup).isBefore(lastPickupFilterAfter)) {
        if (DEBUG) console.info('vehicle fails lastPickup after filter', vehicle.humanId);
        return false;
      }
    }
    if (lastDeployFilterBefore) {
      if ((!lastDeploy || moment(lastDeploy).isAfter(lastDeployFilterBefore))
        && (lastDeployFilterAfter || lastDeploy)) {
        if (DEBUG) console.info('vehicle fails lastDeploy before filter', vehicle.humanId);
        return false;
      }
    }
    if (lastDeployFilterAfter) {
      if (!lastDeploy || moment(lastDeploy).isBefore(lastDeployFilterAfter)) {
        if (DEBUG) console.info('vehicle fails lastDeploy after filter', vehicle.humanId);
        return false;
      }
    }
    if (lastTripFilterBefore) {
      if ((!lastTrip || moment(lastTrip).isAfter(lastTripFilterBefore))
        && (lastTripFilterAfter || lastTrip)) {
        if (DEBUG) console.info('vehicle fails lastTrip before filter', vehicle.humanId);
        return false;
      }
    }
    if (lastTripFilterAfter) {
      if (!lastTrip || moment(lastTrip).isBefore(lastTripFilterAfter)) {
        if (DEBUG) console.info('vehicle fails lastTrip after filter', vehicle.humanId);
        return false;
      }
    }
    if (lastCommunicationFilterBefore) {
      if ((!lastCommunication || moment(lastCommunication).isAfter(lastCommunicationFilterBefore))
          && (lastCommunicationFilterAfter || lastCommunication)) {
        if (DEBUG) console.info('vehicle fails lastCommunication before filter', vehicle.humanId);
        return false;
      }
    }
    if (lastCommunicationFilterAfter) {
      if (!lastCommunication || moment(lastCommunication).isBefore(lastCommunicationFilterAfter)) {
        if (DEBUG) console.info('vehicle fails lastCommunication after filter', vehicle.humanId);
        return false;
      }
    }
    if (lastRiderScanFilterBefore) {
      if ((!lastScanTime || moment(lastScanTime).isAfter(lastRiderScanFilterBefore))
          && (lastRiderScanFilterAfter || lastScanTime)) {
        if (DEBUG) console.info('vehicle fails lastRiderScan before filter', vehicle.humanId);
        return false;
      }
    }
    if (lastRiderScanFilterAfter) {
      if (!lastScanTime || moment(lastScanTime).isBefore(lastRiderScanFilterAfter)) {
        if (DEBUG) console.info('vehicle fails lastRiderScan after filter', vehicle.humanId);
        return false;
      }
    }
    if (minSearchAttemptsFilter) {
      if (hasSearchAttempts) {
        const isLowerThanMinFilter = searchAttempts.length < minSearchAttemptsFilter;
        if (isLowerThanMinFilter) {
          if (DEBUG) console.info('Vehicle fails min search attempts filter', vehicle.humanId, vehicle.searchAttempts.length);
          return false;
        }
      }
    }
    if (maxSearchAttemptsFilter) {
      if (hasSearchAttempts) {
        const isHigherThanMaxFilter = searchAttempts.length > maxSearchAttemptsFilter;
        if (isHigherThanMaxFilter) {
          if (DEBUG) console.info('Vehicle fails max search attempts filter', vehicle.humanId, vehicle.searchAttempts.length);
          return false;
        }
      }
    }
    if (minSearchAttemptsFilter && maxSearchAttemptsFilter) {
      if (hasSearchAttempts) {
        const isLowerThanMinFilter = searchAttempts.length < minSearchAttemptsFilter;
        const isHigherThanMaxFilter = searchAttempts.length > maxSearchAttemptsFilter;
        if (isLowerThanMinFilter || isHigherThanMaxFilter) {
          if (DEBUG) console.info('Vehicle fails search attempts filter', vehicle.humanId, vehicle.searchAttempts.length);
          return false;
        }
      }
    }
    if (lastReportedNetworkFilter && lastReportedNetworkFilter.length > 0) {
      const hasNetwork = vehicle.lastReportedNetwork
        && lastReportedNetworkFilter.includes(vehicle.lastReportedNetwork);
      if (!hasNetwork) {
        if (DEBUG) console.info('vehicle fails network search filter', vehicle.humanId);
        return false;
      }
    }
    if (geofenceLayersFilter && geofenceLayersFilter.length > 0) {
      const vehicleLayers = vehicle.zoneCategories || [];
      const hasMatchingLayer = vehicleLayers.filter((l) => geofenceLayersFilter.includes(l));
      if (hasMatchingLayer.length === 0) {
        if (DEBUG) console.info('vehicle fails geofence layers filter', vehicle.humanId);
        return false;
      }
    }
    if (locationFilter && locationFilter.length > 0) {
      const inCompliantHub = locationFilter === LOCATION_FILTER_FIELDS.IN_COMPLIANT_HUB;
      const inOverCapacityHub = locationFilter === LOCATION_FILTER_FIELDS.IN_OVER_CAPACITY_HUB;
      const outsideOfHubs = locationFilter === LOCATION_FILTER_FIELDS.OUTSIDE_OF_HUBS;
      if (inCompliantHub) {
        if (isNil(locationId) || (!isNil(locationId) && overCapacity)) {
          if (DEBUG) console.info('vehicle fails location filter, at compliant hub', vehicle.humanId, locationId);
          return false;
        }
      }
      if (inOverCapacityHub) {
        if (isNil(locationId) || (!isNil(locationId) && !overCapacity)) {
          if (DEBUG) console.info('vehicle fails location filter, at overcapacity hub', vehicle.humanId, locationId);
          return false;
        }
      }
      if (outsideOfHubs) {
        if (!isNil(locationId)) {
          if (DEBUG) console.info('vehicle fails location filter, outside of hub', vehicle.humanId, locationId);
          return false;
        }
      }
    }
    if (hasDefaultGpkgFilter && hasDefaultGpkgFilter.length > 0) {
      const hasSameGpkg = hasDefaultGpkgFilter.includes(HAS_DEFAULT_GPKG_FILTER_FIELDS.SAME);
      const hasNoSameGpkg = hasDefaultGpkgFilter.includes(HAS_DEFAULT_GPKG_FILTER_FIELDS.NOT_SAME);
      if (hasSameGpkg) {
        if (!hasMarketDefaultGeofencePackage) {
          if (DEBUG) console.info('vehicle fails same default geofence package filter', vehicle.humanId, hasMarketDefaultGeofencePackage);
          return false;
        }
      }
      if (hasNoSameGpkg) {
        if (hasMarketDefaultGeofencePackage) {
          if (DEBUG) console.info('vehicle fails no same default geofence package filter', vehicle.humanId, hasMarketDefaultGeofencePackage);
          return false;
        }
      }
    }
    return true;
  });
}

export function isBetaVersion() {
  const isBeta = process.env.REACT_APP_BETA;
  return isBeta === 'true' || false;
}

const getSizeInBytes = (value) => new Blob([JSON.stringify(value)], { type: 'text/plain' }).size;

export const splitArrays = (array, maxSizeInBytes) => {
  const arrays = [];

  let currentArray = [];
  let currentArraySize = 0;

  array.forEach((value) => {
    const size = getSizeInBytes(value);

    // if current value exceeds the limit — create new sub array
    if (currentArraySize + size > maxSizeInBytes) {
      arrays.push(currentArray);
      currentArray = [];
      currentArraySize = 0;
    }

    // add current value to current sub array
    currentArray.push(value);
    currentArraySize += size;
  });

  if (currentArray.length) {
    arrays.push(currentArray);
  }

  return arrays;
};

export const dashboardPage = {
  structure: {
    fleet: ['uptime', 'nonRentable', 'marketCap', 'rentable', 'useable', 'notAvailable'],
    scooters: ['offline', 'lost', 'missing', 'warehoused', 'outdated'],
    tasks: ['riderScan', 'withTasks', 'ready'],
  },
  data: {
    uptime: {
      title: 'Uptime',
      tooltip: 'Rentable divided by useable vehicles',
      actionText: 'Deploy more scooters or pick up scooters with tasks to increase your market\'s uptime.',
    },
    nonRentable: {
      title: 'Non-rentable',
      tooltip: 'All vehicles that are not Reserved/Available divided by useable vehicles',
      // link: 'deployment=warehouse&possession=in_possession&extStatus=operator%2Cout_of_service%2Cunavailable%2Coffline%2Cself_check%2Ccharging%2Cfirmware_update%2Cmismatch',
      actionText: 'Deploy more scooters or pick up scooters with tasks to increase your market\'s uptime.',
    },
    marketCap: {
      title: 'Uptime/\nmarket cap',
      tooltip: 'Rentable divided by market cap',
      actionText: [
        'Deploy more scooters or pick up scooters with tasks to increase your market\'s uptime.',
        'Your market is at risk of incompliance, pick up scooters or update the market capacity.',
      ],
    },
    rentable: {
      title: 'Rentable vehicles',
      tooltip: 'Available/Reserved - In Field vehicles',
      link: 'deployment=in_field&possession=in_possession&extStatus=available%2Creserved',
    },
    useable: {
      title: 'Useable vehicles',
      tooltip: 'All vehicles that are not decommissioned',
      link: 'deployment=in_field%2Cwarehouse&possession=in_possession',
    },
    notAvailable: {
      title: 'Not available in field',
      tooltip: 'All In Field vehicles that are not Available/Reserved',
      link: 'deployment=in_field&possession=in_possession&extStatus=operator%2Cout_of_service%2Cunavailable%2Coffline%2Cself_check%2Ccharging%2Cfirmware_update%2Cmismatch',
    },
    offline: {
      title: 'Offline w/o search attempt',
      tooltip: 'Offline vehicles with 0 search attempt',
      link: 'deployment=in_field&extStatus=offline&maxSearchAttempts=0',
      actionText: 'Assign OAs to search and pick up scooters in this category.',
    },
    lost: {
      title: 'Almost lost',
      tooltip: 'Vehicles that have been missing for 21-28 days',
      link: `deployment=in_field&possession=in_possession%2Cmissing%2Cinaccessible&extStatus=offline&communicationAfter=${moment().subtract(29, 'days').valueOf()}&communicationBefore=${moment().subtract(21, 'days').valueOf()}`,
      actionText: 'Assign OAs to search and pick up scooters in this category.',
    },
    missing: {
      title: 'Almost missing',
      tooltip: 'Vehicles that have been offline for 2-6 days',
      link: `deployment=in_field&possession=in_possession%2Clost%2Cinaccessible&extStatus=offline&communicationAfter=${moment().subtract(7, 'days').valueOf()}&communicationBefore=${moment().subtract(4, 'days').valueOf()}`,
      actionText: 'Assign OAs to search and pick up scooters in this category.',
    },
    warehoused: {
      title: 'Available - Reserved/\nWarehouse',
      tooltip: 'Available/Reserved vehicles with Warehouse status',
      // link: 'deployment=warehouse&extStatus=available%2Creserved&geofenceLayers=Operational+Area%2CNo+Parking%2CNo+Scooter%2CNo+Riding%2CSlow+Riding%2CNo+Parking%2C+Slow+Riding%2CParking+Area%2CLimited+Parking%2CParking+Incentive',
      actionText: 'Correct the status with the Bulk Action button in the Vehicle page',
    },
    outdated: {
      title: 'Outdated geofence pkg.',
      titleExp: 'Outdated geofence package',
      tooltip: 'Vehicles whose geofence package is different from the market\'s default package',
      link: `deployment=in_field%2Cwarehouse&possession=in_possession&extStatus=operator%2Cout_of_service%2Cunavailable%2Cself_check%2Ccharging%2Cfirmware_update%2Cmismatch%2Cavailable&communicationAfter=${moment().subtract(6, 'hours').valueOf()}&hasDefaultGpkg=Not+the+same+as+the+market+default`,
      actionText: 'Use the OTA page to update these scooters\' geofence package.',
    },
    riderScan: {
      title: 'Outstanding rider scan',
      tooltip: 'Offline vehicles with rider scan',
      link: 'extStatus=offline&pickupReasons=Rider+Scan',
      actionText: 'Assign OAs to search and pick up scooters in this category.',
    },
    withTasks: {
      title: 'Scooters with tasks',
      tooltip: 'Number of scooters with pickup',
      link: 'pickupFields=all_pickups',
      actionText: 'Assign OAs to pick up scooters in this category.',
    },
    ready: {
      title: 'Ready for deployment',
      tooltip: 'Operator - Warehouse scooters that have >80% battery',
      link: `deployment=warehouse&possession=in_possession&extStatus=operator%2Ccharging&minBattery=80&communicationAfter=${moment().subtract(12, 'hours').valueOf()}`,
    },
  },
  colors: { // from MaterialUI error, warning and success palettes
    red: '#ef5350',
    lightred: '#ef5350',
    orange: '#ffb74d',
    lightgreen: '#4caf50',
    green: '#4caf50',
  },
  sortOrder: [
    'uptime',
    'marketCap',
    'outdated',
    'offline',
    'lost',
    'missing',
    'warehoused',
    'riderScan',
    'withTasks',
    'nonRentable',
  ],
};
